import { useCallback, useEffect, useRef, useState } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'

export const MOS_TECHNOLOGIES_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed'
}

const createDeferred = () => {
  let settle = () => {}
  const promise = new Promise((resolve) => { settle = resolve })
  return { promise, resolve: settle }
}

/**
 * Story 193 (MIC-4346, RN7/D4): the catalog of ACTIVE technologies of the installation, from the MOS
 * route `GET mos/technologies` (`[{ id, name }]`, sorted by name by Sun) -- never from
 * `technologiesBasicData`, which needs the technology-management permission.
 *
 * It feeds the "All Jobs" technology selector of the configuration panel, and tells the dialog tab
 * and the widget's main view which saved technologies still count (P4: the filter itself is Sun's).
 * Nothing is requested until `enabled` (S21/S22/S24): a work centre that never turned the tab on
 * pays no extra request.
 *
 * `whenSettled()` returns a promise that resolves with the catalog once it has answered (loaded or
 * failed). The tables' `dataFetch` awaits it, so while the catalog loads the table shows only its own
 * row spinner and the list is not requested yet (RN5.4), without remounting the table (R5).
 */
const useMosTechnologies = ({ enabled = false } = {}) => {
  const [catalog, setCatalog] = useState({ status: MOS_TECHNOLOGIES_STATUS.IDLE, technologies: [] })
  const settledRef = useRef(createDeferred())
  const requestedRef = useRef(false)

  useEffect(() => {
    if (!enabled || requestedRef.current) return
    requestedRef.current = true
    // No "mounted" guard: StrictMode's mount-unmount-mount would drop the only answer, since the
    // remount skips the request.
    setCatalog({ status: MOS_TECHNOLOGIES_STATUS.LOADING, technologies: [] })
    const load = async () => {
      let settled
      try {
        const response = await apiGet('mos/technologies')
        settled = { status: MOS_TECHNOLOGIES_STATUS.LOADED, technologies: Array.isArray(response) ? response : [] }
      } catch (err) {
        console.error('Error fetching MOS technologies:', err)
        settled = { status: MOS_TECHNOLOGIES_STATUS.FAILED, technologies: [] }
      }
      settledRef.current.resolve(settled)
      setCatalog(settled)
    }
    load()
  }, [enabled])

  const whenSettled = useCallback(() => settledRef.current.promise, [])

  return {
    status: catalog.status,
    technologies: catalog.technologies,
    failed: catalog.status === MOS_TECHNOLOGIES_STATUS.FAILED,
    settled: catalog.status === MOS_TECHNOLOGIES_STATUS.LOADED || catalog.status === MOS_TECHNOLOGIES_STATUS.FAILED,
    whenSettled
  }
}

export default useMosTechnologies
